import { ScrollConfig } from '../types';

export interface ScrollableContainer {
	element: HTMLElement;
	scrollLeft: number;
	scrollTop: number;
	scrollWidth: number;
	scrollHeight: number;
	clientWidth: number;
	clientHeight: number;
	scrollDirections: {
		horizontal: boolean;
		vertical: boolean;
	};
}

interface ScrollTarget {
	element: HTMLElement;
	speedX: number;
	speedY: number;
	canScrollHorizontal: boolean;
	canScrollVertical: boolean;
}

export class ScrollManager {
	private config: Required<ScrollConfig>;
	private isActive: boolean = false;
	private scrollContainer: HTMLElement | null = null;
	private animationFrameId: number | null = null;

	private currentSpeedX: number = 0;
	private currentSpeedY: number = 0;

	private lastCursorX: number = 0;
	private lastCursorY: number = 0;
	private lastUpdateTime: number = 0;

	private lastRealCursorX: number = 0;
	private lastRealCursorY: number = 0;
	private isManualSpeedUpdate: boolean = false;

	private scrollTargets: ScrollTarget[] = [];
	private scrollTargetElement: HTMLElement | null = null;

	constructor(config?: Partial<ScrollConfig>) {
		this.config = {
			edgeThreshold: 50,
			scrollSpeed: 1,
			maxSpeed: 20,
			enabled: true,
			scrollableSelectors: [], // НОВОЕ: селекторы для поиска скроллящихся контейнеров
			...config
		};
	}

	/**
	 * Начинает отслеживание для автокролла
	 */
	start(container?: HTMLElement): void {
		if (!this.config.enabled) return;

		this.scrollContainer = container || document.documentElement;
		this.isActive = true;
		this.lastUpdateTime = performance.now();
	}

	/**
	 * Обновляет позицию курсора и активирует автокролл если нужно
	 */
	update(cursorX: number, cursorY: number): void {
		if (!this.isActive || !this.config.enabled) return;

		// Проверяем, действительно ли курсор двигался
		const cursorMoved =
			Math.abs(cursorX - this.lastRealCursorX) > 1 ||
			Math.abs(cursorY - this.lastRealCursorY) > 1;

		if (cursorMoved) {
			// Запоминаем РЕАЛЬНЫЕ координаты курсора
			this.lastRealCursorX = cursorX;
			this.lastRealCursorY = cursorY;
			this.isManualSpeedUpdate = true;

			// Ищем новые контейнеры для скролла
			this.scrollTargets = this.findScrollContainers(cursorX, cursorY);
		}

		this.lastCursorX = cursorX;
		this.lastCursorY = cursorY;

		if (this.scrollTargets.length > 0) {
			// ОБНОВЛЯЕМ скорость ТОЛЬКО если курсор двигался
			if (cursorMoved) {
				this.calculateScrollSpeeds(cursorX, cursorY);
			}

			// Проверяем, есть ли хоть у одного контейнера ненулевая скорость
			const hasAnySpeed = this.scrollTargets.some(
				target => target.speedX !== 0 || target.speedY !== 0
			);

			if (hasAnySpeed) {
				this.startScrollAnimation();
			} else {
				this.stopScrollAnimation();
			}
		} else {
			this.stopScrollAnimation();
			this.scrollTargets = [];
		}
	}

	/**
	 * Останавливает автокролл
	 */
	stop(): void {
		this.isActive = false;
		this.currentSpeedX = 0;
		this.currentSpeedY = 0;
		this.scrollTargets = [];
		this.scrollTargetElement = null;
		this.isManualSpeedUpdate = false;

		this.stopScrollAnimation();
	}

	/**
	 * Проверяет, активен ли автокролл
	 */
	isScrolling(): boolean {
		return this.isActive && (this.currentSpeedX !== 0 || this.currentSpeedY !== 0);
	}

	/**
	 * Возвращает текущую скорость скролла
	 */
	getCurrentSpeed(): { x: number; y: number } {
		return { x: this.currentSpeedX, y: this.currentSpeedY };
	}

	/**
	 * Возвращает текущий элемент, который скроллится
	 */
	getScrollTarget(): HTMLElement | null {
		return this.scrollTargetElement;
	}

	/**
	 * Возвращает все текущие скроллящиеся элементы
	 */
	getScrollTargets(): HTMLElement[] {
		return this.scrollTargets.map(target => target.element);
	}

	// ========== PRIVATE METHODS ==========

	/**
	 * Находит ВСЕ контейнеры для скролла по селекторам
	 * Возвращает массив всех контейнеров, которые содержат курсор
	 */
	private findScrollContainers(x: number, y: number): ScrollTarget[] {
		const targets: ScrollTarget[] = [];

		// Всегда добавляем дефолтный контейнер если он есть
		if (this.scrollContainer) {
			const defaultContainer = this.scrollContainer;
			if (this.isCursorNearEdge(defaultContainer, x, y)) {
				targets.push({
					element: defaultContainer,
					speedX: 0,
					speedY: 0,
					canScrollHorizontal: this.canScrollHorizontally(defaultContainer),
					canScrollVertical: this.canScrollVertically(defaultContainer)
				});
			}
		}

		// Ищем по всем селекторам из конфига
		for (const selector of this.config.scrollableSelectors) {
			try {
				const elements = document.querySelectorAll(selector);
				for (const element of elements) {
					if (!(element instanceof HTMLElement)) continue;

					const rect = element.getBoundingClientRect();

					// Проверяем что курсор внутри элемента
					if (x >= rect.left && x <= rect.right &&
						y >= rect.top && y <= rect.bottom) {

						// Проверяем что курсор близко к краю
						if (this.isCursorNearEdge(element, x, y)) {
							// Проверяем дубликаты
							if (!targets.some(t => t.element === element)) {
								targets.push({
									element,
									speedX: 0,
									speedY: 0,
									canScrollHorizontal: this.canScrollHorizontally(element),
									canScrollVertical: this.canScrollVertically(element)
								});
							}
						}
					}
				}
			} catch (error) {
				// Игнорируем ошибки селекторов
				console.warn(`Invalid selector or element: ${selector}`, error);
			}
		}

		// Сортируем по вложенности: самые глубокие контейнеры первыми
		return targets.sort((a, b) => {
			const aDepth = this.getElementDepth(a.element);
			const bDepth = this.getElementDepth(b.element);
			return bDepth - aDepth; // Большая глубина = выше приоритет
		});
	}

	private getElementDepth(element: HTMLElement): number {
		let depth = 0;
		let current: HTMLElement | null = element;
		while (current && current !== document.body) {
			depth++;
			current = current.parentElement;
		}
		return depth;
	}

	/**
	 * Проверяет, находится ли курсор рядом с краями элемента
	 */
	private isCursorNearEdge(element: HTMLElement, x: number, y: number): boolean {
		const rect = element.getBoundingClientRect();
		const { edgeThreshold } = this.config;

		return (
			x < rect.left + edgeThreshold ||      // близко к левому краю
			x > rect.right - edgeThreshold ||     // близко к правому краю
			y < rect.top + edgeThreshold ||       // близко к верхнему краю
			y > rect.bottom - edgeThreshold       // близко к нижнему краю
		);
	}

	private canScrollHorizontally(element: HTMLElement): boolean {
		return element.scrollWidth > element.clientWidth;
	}

	private canScrollVertically(element: HTMLElement): boolean {
		return element.scrollHeight > element.clientHeight;
	}

	private calculateScrollSpeeds(x: number, y: number): void {
		const { edgeThreshold, scrollSpeed, maxSpeed } = this.config;

		for (const target of this.scrollTargets) {
			const { element: container, canScrollHorizontal, canScrollVertical } = target;
			const rect = container.getBoundingClientRect();

			// Сбрасываем скорость для этого контейнера
			target.speedX = 0;
			target.speedY = 0;

			// Проверяем, находится ли курсор ВНУТРИ контейнера
			const isCursorInsideX = x >= rect.left && x <= rect.right;
			const isCursorInsideY = y >= rect.top && y <= rect.bottom;

			// Горизонтальный скролл (только если контейнер может скроллиться горизонтально)
			if (canScrollHorizontal && isCursorInsideX) {
				const distanceToLeft = x - rect.left;
				const distanceToRight = rect.right - x;

				if (distanceToLeft < edgeThreshold) {
					const ratio = (edgeThreshold - distanceToLeft) / edgeThreshold;
					target.speedX = -this.calculateSpeedForDistance(ratio, scrollSpeed, maxSpeed);
				} else if (distanceToRight < edgeThreshold) {
					const ratio = (edgeThreshold - distanceToRight) / edgeThreshold;
					target.speedX = this.calculateSpeedForDistance(ratio, scrollSpeed, maxSpeed);
				}
			}

			// Вертикальный скролл (только если контейнер может скроллиться вертикально)
			if (canScrollVertical && isCursorInsideY) {
				const distanceToTop = y - rect.top;
				const distanceToBottom = rect.bottom - y;

				if (distanceToTop < edgeThreshold) {
					const ratio = (edgeThreshold - distanceToTop) / edgeThreshold;
					target.speedY = -this.calculateSpeedForDistance(ratio, scrollSpeed, maxSpeed);
				} else if (distanceToBottom < edgeThreshold) {
					const ratio = (edgeThreshold - distanceToBottom) / edgeThreshold;
					target.speedY = this.calculateSpeedForDistance(ratio, scrollSpeed, maxSpeed);
				}
			}

			// Корректируем скорость если достигли границ скролла
			if (target.speedX < 0 && container.scrollLeft <= 0) {
				target.speedX = 0;
			} else if (target.speedX > 0 && container.scrollLeft >= container.scrollWidth - container.clientWidth) {
				target.speedX = 0;
			}

			if (target.speedY < 0 && container.scrollTop <= 0) {
				target.speedY = 0;
			} else if (target.speedY > 0 && container.scrollTop >= container.scrollHeight - container.clientHeight) {
				target.speedY = 0;
			}
		}
	}

	private calculateSpeedForDistance(ratio: number, baseSpeed: number, maxSpeed: number): number {
		// Экспоненциальное увеличение скорости при приближении к краю
		const speed = baseSpeed * Math.pow(ratio, 0.7) * 15;
		return Math.min(speed, maxSpeed);
	}

	private startScrollAnimation(): void {
		// Если уже скроллим, НЕ ПЕРЕЗАПУСКАЕМ анимацию
		if (this.animationFrameId) return;

		const animate = () => {
			if (!this.isActive) {
				this.stopScrollAnimation();
				return;
			}

			// Проверяем, есть ли хоть у одного контейнера ненулевая скорость
			let hasAnyActiveTarget = false;

			for (const target of this.scrollTargets) {
				const { element: container, speedX, speedY } = target;

				// Проверяем достигли ли границ скролла для этого контейнера
				if (speedX < 0 && container.scrollLeft <= 0) {
					target.speedX = 0;
				} else if (speedX > 0 && container.scrollLeft >= container.scrollWidth - container.clientWidth) {
					target.speedX = 0;
				}

				if (speedY < 0 && container.scrollTop <= 0) {
					target.speedY = 0;
				} else if (speedY > 0 && container.scrollTop >= container.scrollHeight - container.clientHeight) {
					target.speedY = 0;
				}

				// Применяем скролл с ПОСТОЯННОЙ скоростью для каждого контейнера
				if (speedX !== 0 || speedY !== 0) {
					hasAnyActiveTarget = true;
					container.scrollLeft += speedX;
					container.scrollTop += speedY;
				}
			}

			// КРИТИЧНО: НЕ пересчитываем скорость здесь!
			// Скорость остается постоянной пока не придет новый update()

			if (!hasAnyActiveTarget) {
				this.stopScrollAnimation();
				return;
			}

			// Запускаем следующий кадр
			this.animationFrameId = requestAnimationFrame(animate);
		};

		this.animationFrameId = requestAnimationFrame(animate);
	}

	private stopScrollAnimation(): void {
		if (this.animationFrameId) {
			cancelAnimationFrame(this.animationFrameId);
			this.animationFrameId = null;
		}
	}
}